import React from "react";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";

type ToastTone = "default" | "success" | "error";

type QueuedToast = {
  id: string;
  title: string;
  description: string;
  tone: ToastTone;
};

export type AppToastContextValue = {
  pushToast: (opts: {
    title: string;
    description: string;
    tone: ToastTone;
  }) => void;
  showError: (description: string, title?: string) => void;
};

const AppToastContext = React.createContext<AppToastContextValue | null>(null);

const AUTO_DISMISS_MS = 5000;

export function useAppToast(): AppToastContextValue {
  const ctx = React.useContext(AppToastContext);
  if (!ctx) {
    throw new Error("useAppToast must be used within AppToastProvider");
  }
  return ctx;
}

export const AppToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<QueuedToast[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = React.useCallback(
    (opts: { title: string; description: string; tone: ToastTone }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      setToasts((prev) => [...prev, { id, ...opts }]);
    },
    [],
  );

  const showError = React.useCallback(
    (description: string, title = "Something went wrong") => {
      pushToast({ title, description, tone: "error" });
    },
    [pushToast],
  );

  const value = React.useMemo(
    () => ({ pushToast, showError }),
    [pushToast, showError],
  );

  return (
    <AppToastContext.Provider value={value}>
      <ToastProvider duration={AUTO_DISMISS_MS}>
        {children}
        {toasts.map((t) => (
          <Toast
            key={t.id}
            open
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
            variant={t.tone}
          >
            <div className="grid gap-1">
              <ToastTitle>{t.title}</ToastTitle>
              <ToastDescription>{t.description}</ToastDescription>
            </div>
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </AppToastContext.Provider>
  );
};
